import React, { useState, useMemo } from "react";
import { firestore } from "./../firebase";
import {
    FaSearch, FaImage, FaArrowUp, FaArrowDown, FaUsers,
    FaEdit, FaTrash, FaChevronDown, FaTimes, FaExclamationCircle
} from "react-icons/fa";

const UsersTab = ({ users, online, editingUser, setEditingUser }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState("");
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [sortField, setSortField] = useState("username");
    const [sortOrder, setSortOrder] = useState("asc");
    const [filterType, setFilterType] = useState("all");
    const [showOnlyCustomImages, setShowOnlyCustomImages] = useState(false);
    const [showOnlyPremium, setShowOnlyPremium] = useState(false);
    const [displayLimit, setDisplayLimit] = useState(50);

    const filteredAndSortedUsers = useMemo(() => {
        let result = users.filter((user) => {
            const searchStr = searchQuery.toLowerCase();
            const matchesSearch = (
                user.username?.toLowerCase().includes(searchStr) ||
                user.fullName?.toLowerCase().includes(searchStr) ||
                user.id?.toLowerCase().includes(searchStr) ||
                user.email?.toLowerCase().includes(searchStr)
            );
            const matchesType = filterType === "all" || user.type === filterType;

            const imgUrl = (user.profileImage || user.avatar || "").toLowerCase();
            const hasCustomImage = imgUrl.includes("firebasestorage") || imgUrl.includes("appspot.com");
            const matchesImageFilter = !showOnlyCustomImages || hasCustomImage;

            const matchesPremium = !showOnlyPremium || user.hasPremium === true;

            return matchesSearch && matchesType && matchesImageFilter && matchesPremium;
        });

        result.sort((a, b) => {
            let valA = a[sortField] || 0;
            let valB = b[sortField] || 0;

            if (sortField === "classic" || sortField === "frenzy" || sortField === "hp") {
                valA = Number(valA);
                valB = Number(valB);
            } else if (sortField === "friends") {
                valA = (a.friends || []).length;
                valB = (b.friends || []).length;
            } else if (typeof valA === 'string') {
                valA = valA.toLowerCase();
                valB = (valB || "").toLowerCase();
            }

            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });

        return result;
    }, [users, searchQuery, filterType, showOnlyCustomImages, showOnlyPremium, sortField, sortOrder]);

    const displayedUsers = useMemo(() => {
        return filteredAndSortedUsers.slice(0, displayLimit);
    }, [filteredAndSortedUsers, displayLimit]);

    const totalWithCustomImages = useMemo(() => {
        return users.filter(u => {
            const img = (u.profileImage || u.avatar || "").toLowerCase();
            return img.includes("firebasestorage") || img.includes("appspot.com");
        }).length;
    }, [users]);

    const handleLoadMore = () => {
        setDisplayLimit(prev => prev + 50);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        try {
            await firestore.collection("Users").doc(editingUser.id).update({
                username: editingUser.username,
                fullName: editingUser.fullName,
                classic: Number(editingUser.classic || 0),
                frenzy: Number(editingUser.frenzy || 0),
                hp: Number(editingUser.hp || 0),
                million: Number(editingUser.million || 0),
                hasPremium: editingUser.hasPremium === true
            });
            setEditingUser(null);
            alert("User profile and scores updated successfully!");
        } catch (error) {
            console.error("Error updating user:", error);
            alert("Error updating user: " + error.message);
        }
    };

    const handleDeleteUser = async () => {
        if (deleteConfirmation !== "DELETE") {
            alert("Please type 'DELETE' to confirm.");
            return;
        }
        try {
            await firestore.collection("Users").doc(showDeleteModal.id).delete();
            setShowDeleteModal(null);
            setDeleteConfirmation("");
            alert("User deleted permanently.");
        } catch (error) {
            console.error("Error deleting user:", error);
            alert("Error deleting user: " + error.message);
        }
    };

    const isUserOnline = (userId) => {
        return online.some((o) => o.id === userId);
    };

    const getFirestoreLink = (userId) => {
        return `https://console.firebase.google.com/u/0/project/trivia-rn/firestore/data/~2FUsers~2F${userId}`;
    };

    return (
        <div className="users-tab">
            <div className="block" style={{ marginTop: "0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", gap: "1rem", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <h1 style={{ margin: 0 }}>User Management ({filteredAndSortedUsers.length} / {users.length})</h1>
                        <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "600" }}>
                            Total with custom images: {totalWithCustomImages}
                        </span>
                    </div>
                    <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ position: "relative" }}>
                            <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "1.1rem" }} />
                            <input
                                type="text"
                                placeholder="Search all users..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setDisplayLimit(50);
                                }}
                                style={{ padding: "0.6rem 1rem 0.6rem 2.5rem", borderRadius: "10px", border: "1px solid #e2e8f0", width: "200px" }}
                            />
                        </div>
                        <select
                            value={filterType}
                            onChange={(e) => {
                                setFilterType(e.target.value);
                                setDisplayLimit(50);
                            }}
                            style={{ padding: "0.6rem", borderRadius: "10px", border: "1px solid #e2e8f0", background: "white" }}
                        >
                            <option value="all">All Login Types</option>
                            <option value="google">Google</option>
                            <option value="apple">Apple</option>
                            <option value="anonymous">Anonymous</option>
                        </select>

                        <button
                            onClick={() => {
                                setShowOnlyCustomImages(!showOnlyCustomImages);
                                setDisplayLimit(50);
                            }}
                            style={{
                                padding: "0.6rem 1rem",
                                borderRadius: "10px",
                                border: "1px solid #e2e8f0",
                                background: showOnlyCustomImages ? "var(--primary)" : "white",
                                color: showOnlyCustomImages ? "white" : "#475569",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontWeight: "600",
                                transition: "all 0.2s"
                            }}
                        >
                            {showOnlyCustomImages ? <FaImage /> : <FaImage style={{ opacity: 0.5 }} />}
                             Custom Only
                        </button>

                        {/* Premium filter */}
                        <button
                            onClick={() => {
                                setShowOnlyPremium(!showOnlyPremium);
                                setDisplayLimit(50);
                            }}
                            style={{
                                padding: "0.6rem 1rem",
                                borderRadius: "10px",
                                border: showOnlyPremium ? "1.5px solid #b7860b" : "1px solid #e2e8f0",
                                background: showOnlyPremium ? "#fef9c3" : "white",
                                color: showOnlyPremium ? "#92400e" : "#475569",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontWeight: "700",
                                transition: "all 0.2s"
                            }}
                        >
                            ⭐ Premium Only
                        </button>

                        <select
                            value={sortField}
                            onChange={(e) => setSortField(e.target.value)}
                            style={{ padding: "0.6rem", borderRadius: "10px", border: "1px solid #e2e8f0", background: "white" }}
                        >
                            <option value="username">Sort by Name</option>
                            <option value="classic">Sort by Classic</option>
                            <option value="frenzy">Sort by Frenzy</option>
                            <option value="hp">Sort by Harry Potter</option>
                            <option value="friends">Sort by Friends Count</option>
                            <option value="lastOnline">Sort by Last Login</option>
                        </select>
                        <button
                            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                            style={{ padding: "0.6rem", borderRadius: "10px", border: "1px solid #e2e8f0", background: "white", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                            {sortOrder === "asc" ? <FaArrowUp /> : <FaArrowDown />}
                        </button>
                    </div>
                </div>

                <div className="blockTableContainer">
                    <table className="blockTable usersTable">
                        <thead>
                            <tr>
                                <th>Profile</th>
                                <th>Scores</th>
                                <th>Friends</th>
                                <th>Last Active</th>
                                <th>Account</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedUsers.map((user) => (
                                <tr
                                    key={user.id}
                                    style={user.hasPremium ? {
                                        background: "linear-gradient(90deg, #fefce8 0%, #fffbeb 100%)",
                                        boxShadow: "inset 3px 0 0 #f5c518, 0 1px 3px rgba(245,197,24,0.15)"
                                    } : {}}
                                >
                                    <td className="user-profile-cell">
                                        <div className="profile-container">
                                            <div
                                                className="avatar-wrapper"
                                                onClick={() => setEnlargedImage(user.profileImage || user.avatar || "/trivia-logo.png")}
                                            >
                                                <img
                                                    src={user.profileImage || user.avatar || "/trivia-logo.png"}
                                                    alt="Avatar"
                                                    className="user-avatar"
                                                />
                                                {isUserOnline(user.id) && <div className="online-indicator"></div>}
                                            </div>
                                            <div className="user-info">
                                                <a
                                                    href={getFirestoreLink(user.id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="username-link"
                                                    title="Open in Firestore Console"
                                                >
                                                    {user.username}
                                                </a>
                                                <span className="full-name">{user.fullName || "No Name"}</span>
                                                {user.email && <span className="email">{user.email}</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="scores-grid">
                                            <div className="score-pill classic" title="Classic Score">
                                                <span className="label">CL</span>
                                                <span className="value">{user.classic || 0}</span>
                                            </div>
                                            <div className="score-pill frenzy" title="Frenzy Score">
                                                <span className="label">FZ</span>
                                                <span className="value">{user.frenzy || 0}</span>
                                            </div>
                                            {(user.hp !== undefined) && (
                                                <div className="score-pill hp" title="Harry Potter Score">
                                                    <span className="label">HP</span>
                                                    <span className="value">{user.hp}</span>
                                                </div>
                                            )}
                                            {(user.million !== undefined) && (
                                                <div className="score-pill million" title="Million Score">
                                                    <span className="label">1M</span>
                                                    <span className="value">{user.million}</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="friends-badge">
                                            <FaUsers />
                                            <span>{(user.friends || []).length}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="last-active-cell">
                                            <span className="date">
                                                {user.lastOnline ? (user.lastOnline.toDate ? user.lastOnline.toDate().toLocaleDateString() : new Date(user.lastOnline).toLocaleDateString()) : "---"}
                                            </span>
                                            <span className="time">
                                                {user.lastOnline ? (user.lastOnline.toDate ? user.lastOnline.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date(user.lastOnline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })) : ""}
                                            </span>
                                            {isUserOnline(user.id) && <span className="online-label">Online 现在</span>}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="account-cell">
                                            <span className={`login-type ${user.type || 'anonymous'}`}>
                                                {user.type?.toUpperCase() || "ANON"}
                                            </span>
                                            {user.hasPremium && (
                                                <span style={{
                                                    display: "inline-flex", alignItems: "center", gap: "3px",
                                                    padding: "2px 7px", borderRadius: "4px",
                                                    background: "#fef08a", color: "#78350f",
                                                    fontSize: "0.65rem", fontWeight: "800", letterSpacing: "0.03em"
                                                }}>
                                                    ⭐ PREMIUM
                                                </span>
                                            )}
                                            <span className="user-id-text">{user.id.substring(0, 8)}...</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="actions-cell">
                                            <button className="edit" onClick={() => setEditingUser(user)} title="Edit User">
                                                <FaEdit />
                                            </button>
                                            <button className="delete" onClick={() => setShowDeleteModal(user)} title="Delete User">
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {displayLimit < filteredAndSortedUsers.length && (
                    <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
                        <button
                            onClick={handleLoadMore}
                            style={{
                                padding: "0.8rem 2rem",
                                borderRadius: "12px",
                                border: "none",
                                background: "#f1f5f9",
                                color: "#475569",
                                fontWeight: "700",
                                fontSize: "0.95rem",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                border: "1px solid #e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}
                        >
                            <FaChevronDown />
                            Load More ({filteredAndSortedUsers.length - displayLimit} remaining)
                        </button>
                    </div>
                )}
            </div>

            {/* Modals */}
            {editingUser && (
                <div className="modal-overlay">
                    <div className="modal-content edit-modal-large">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                            <h2 style={{ margin: 0 }}>Edit User Profile</h2>
                            <button className="close-btn" onClick={() => setEditingUser(null)} style={{ background: "transparent", border: "none", fontSize: "1.5rem", cursor: "pointer", display: "flex" }}>
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleEditSave} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                            <div className="form-section">
                                <h3 className="section-title">Identity</h3>
                                <div className="form-group">
                                    <label>Username</label>
                                    <input
                                        type="text"
                                        value={editingUser.username || ""}
                                        onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Full Name</label>
                                    <input
                                        type="text"
                                        value={editingUser.fullName || ""}
                                        onChange={(e) => setEditingUser({ ...editingUser, fullName: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email</label>
                                    <input type="text" value={editingUser.email || "N/A"} disabled style={{ background: "#f8fafc", color: "#64748b" }} />
                                </div>
                                <div className="form-group">
                                    <label>Last Login</label>
                                    <div className="readonly-box">
                                        {editingUser.lastOnline ? (editingUser.lastOnline.toDate ? editingUser.lastOnline.toDate().toLocaleString() : new Date(editingUser.lastOnline).toLocaleString()) : "Never"}
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3 className="section-title">Scores</h3>
                                <div className="form-group">
                                    <label>Classic Score</label>
                                    <input
                                        type="number"
                                        value={editingUser.classic || 0}
                                        onChange={(e) => setEditingUser({ ...editingUser, classic: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Frenzy Score</label>
                                    <input
                                        type="number"
                                        value={editingUser.frenzy || 0}
                                        onChange={(e) => setEditingUser({ ...editingUser, frenzy: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Harry Potter Score</label>
                                    <input
                                        type="number"
                                        value={editingUser.hp || 0}
                                        onChange={(e) => setEditingUser({ ...editingUser, hp: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Million Score</label>
                                    <input
                                        type="number"
                                        value={editingUser.million || 0}
                                        onChange={(e) => setEditingUser({ ...editingUser, million: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-section" style={{ gridColumn: "span 2" }}>
                                <h3 className="section-title">Technical Info</h3>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label>User ID</label>
                                        <div className="readonly-box">{editingUser.id}</div>
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label>Login Type</label>
                                        <div className="readonly-box">{editingUser.type?.toUpperCase() || "ANON"}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Premium toggle */}
                            <div className="form-section" style={{ gridColumn: "span 2" }}>
                                <h3 className="section-title">Premium Access</h3>
                                <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem", borderRadius: "12px", background: editingUser.hasPremium ? "#fefce8" : "#f8fafc", border: editingUser.hasPremium ? "1.5px solid #fde68a" : "1px solid #e2e8f0", transition: "all 0.3s" }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, color: editingUser.hasPremium ? "#92400e" : "#475569", marginBottom: "2px" }}>
                                            {editingUser.hasPremium ? "⭐ Premium – Unlocked" : "🔒 Premium – Locked"}
                                        </div>
                                        <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                                            Grants access to Millionaire &amp; Frenzy modes
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setEditingUser({ ...editingUser, hasPremium: !editingUser.hasPremium })}
                                        style={{
                                            padding: "0.6rem 1.4rem",
                                            borderRadius: "10px",
                                            border: "none",
                                            fontWeight: 700,
                                            fontSize: "0.9rem",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                            background: editingUser.hasPremium ? "#ef4444" : "#f5c518",
                                            color: editingUser.hasPremium ? "white" : "#1a1a1a"
                                        }}
                                    >
                                        {editingUser.hasPremium ? "Revoke Premium" : "Grant Premium"}
                                    </button>
                                </div>
                            </div>

                            <div className="modal-actions" style={{ gridColumn: "span 2", marginTop: "1rem" }}>
                                <button type="button" className="cancel-btn" onClick={() => setEditingUser(null)}>Cancel</button>
                                <button type="submit" className="save-btn">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content delete-modal">
                        <FaExclamationCircle style={{ fontSize: "3.5rem", color: "#ef4444", marginBottom: "1rem" }} />
                        <h2 style={{ color: "#ef4444" }}>Confirm Delete</h2>
                        <p>Type <b>DELETE</b> to confirm deleting<br /><b>{showDeleteModal.username}</b></p>
                        <input
                            type="text"
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            placeholder="Type DELETE..."
                            style={{ width: "100%", padding: "0.8rem", borderRadius: "8px", border: "2px solid #f87171", textAlign: "center", fontSize: "1.1rem", fontWeight: "bold" }}
                        />
                        <div className="modal-actions" style={{ justifyContent: "center" }}>
                            <button className="cancel-btn" onClick={() => setShowDeleteModal(null)}>Go Back</button>
                            <button
                                className="delete-final-btn"
                                onClick={handleDeleteUser}
                                disabled={deleteConfirmation !== "DELETE"}
                                style={{ opacity: deleteConfirmation === "DELETE" ? 1 : 0.5 }}
                            >
                                DELETE PERMANENTLY
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {enlargedImage && (
                <div className="modal-overlay" onClick={() => setEnlargedImage(null)}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <img src={enlargedImage} alt="Enlarged" style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: "12px", border: "4px solid white" }} />
                        <button className="close-img-btn" onClick={() => setEnlargedImage(null)} style={{ position: "absolute", top: "-40px", right: "0", background: "transparent", border: "none", color: "white", fontSize: "2rem", cursor: "pointer" }}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                .usersTable {
                    border-collapse: separate;
                    border-spacing: 0 8px;
                    margin-top: -8px;
                }
                .usersTable thead th {
                    background: var(--primary);
                    color: white;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    padding: 16px 16px;
                    border: none;
                }
                .usersTable thead th:last-child { 
                    border-radius: 12px 0 0 12px; 
                }
                .usersTable thead th:first-child { 
                    border-radius: 0 12px 12px 0; 
                }

                .usersTable tbody tr {
                    background: white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                .usersTable tbody tr:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                    z-index: 10;
                    position: relative;
                }
                .usersTable td {
                    padding: 16px;
                    border: none;
                    vertical-align: middle;
                    color: #1e293b;
                }
                .usersTable td:first-child { border-radius: 12px 0 0 12px; }
                .usersTable td:last-child { border-radius: 0 12px 12px 0; }

                /* User Profile Cell */
                .user-profile-cell .profile-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .avatar-wrapper {
                    position: relative;
                    width: 48px;
                    height: 48px;
                    cursor: pointer;
                    flex-shrink: 0;
                }
                .user-avatar {
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 2px solid #f1f5f9;
                }
                .online-indicator {
                    position: absolute;
                    bottom: 2px;
                    right: 2px;
                    width: 12px;
                    height: 12px;
                    background: #10b981;
                    border: 2px solid white;
                    border-radius: 50%;
                }
                .user-info {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }
                .username-link {
                    font-weight: 700;
                    color: #1e293b;
                    text-decoration: none;
                    font-size: 0.95rem;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .username-link:hover { color: var(--primary); }
                .full-name { font-size: 0.8rem; color: #64748b; }
                .email { font-size: 0.75rem; color: #94a3b8; }

                /* Scores Grid */
                .scores-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 6px;
                    min-width: 140px;
                }
                .score-pill {
                    display: flex;
                    align-items: center;
                    background: #f8fafc;
                    border-radius: 6px;
                    padding: 2px 6px;
                    font-size: 0.7rem;
                    border: 1px solid #f1f5f9;
                }
                .score-pill .label {
                    color: #94a3b8;
                    font-weight: 700;
                    margin-right: 6px;
                    font-size: 0.65rem;
                }
                .score-pill .value {
                    color: #334155;
                    font-weight: 600;
                }
                .score-pill.classic { border-left: 3px solid #3b82f6; }
                .score-pill.frenzy { border-left: 3px solid #ef4444; }
                .score-pill.hp { border-left: 3px solid #8b5cf6; }
                .score-pill.million { border-left: 3px solid #f59e0b; }

                /* Friends Badge */
                .friends-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #f0fdf4;
                    color: #166534;
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-weight: 700;
                    font-size: 0.85rem;
                    border: 1px solid #bbf7d0;
                }

                /* Last Active Cell */
                .last-active-cell {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .last-active-cell .date { font-weight: 600; color: #334155; font-size: 0.85rem; }
                .last-active-cell .time { color: #94a3b8; font-size: 0.75rem; }
                .online-label { 
                    font-size: 0.65rem; color: #10b981; font-weight: 700; margin-top: 2px; 
                }

                /* Account Cell */
                .account-cell {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .login-type {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 0.65rem;
                    font-weight: 800;
                    text-align: center;
                }
                .login-type.google { background: #fee2e2; color: #b91c1c; }
                .login-type.apple { background: #f1f5f9; color: #0f172a; }
                .login-type.anonymous { background: #f8fafc; color: #64748b; }
                .user-id-text { font-family: monospace; font-size: 0.65rem; color: #cbd5e1; }

                /* Actions */
                .actions-cell {
                    display: flex;
                    gap: 8px;
                }
                .actions-cell button {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 1.1rem;
                    color: white; /* Ensure text/icon is purely white */
                }
                .actions-cell button svg {
                    width: 1em;
                    height: 1em;
                    min-width: 1em;
                }
                .actions-cell .edit { background: #4ade80; }
                .actions-cell .edit:hover { background: #22c55e; }
                .actions-cell .delete { background: #f87171; }
                .actions-cell .delete:hover { background: #ef4444; }

                /* Modals */
                .modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.75);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 2000; backdrop-filter: blur(10px);
                }
                .modal-content {
                    background: white; padding: 2rem; border-radius: 20px; width: 450px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
                }
                .edit-modal-large { width: 600px; padding: 2rem; }
                .section-title {
                    font-size: 0.8rem; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.05em;
                    margin-bottom: 1rem; border-bottom: 1px solid #f1f5f9; padding-bottom: 0.5rem;
                }
                .readonly-box {
                    padding: 0.75rem; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
                    font-family: monospace; font-size: 0.8rem; color: #64748b;
                }
                .form-group { margin-bottom: 1rem; }
                .form-group label { display: block; margin-bottom: 0.4rem; font-weight: 600; color: #475569; font-size: 0.85rem; }
                .form-group input, .form-group textarea { 
                    width: 100%; padding: 0.75rem; border-radius: 10px; border: 1px solid #e2e8f0; font-size: 0.95rem;
                }
                .modal-actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1rem; }
                .cancel-btn { background: #f1f5f9; border: none; padding: 0.75rem 1.5rem; border-radius: 10px; cursor: pointer; font-weight: 600; color: #64748b; }
                .save-btn { background: var(--primary); color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 10px; cursor: pointer; font-weight: 700; }
                .delete-modal { text-align: center; }
                .delete-final-btn { background: #ef4444; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 10px; cursor: pointer; font-weight: 700; }
            `}</style>
        </div>
    );
};

export default UsersTab;
